import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Box,
  HStack,
  VStack,
  IconButton,
  Text,
  Badge,
  AspectRatio,
  Image,
  useToast
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaDownload, FaVideo, FaShare } from 'react-icons/fa';

const MotionImage = motion(Image);

/**
 * Reusable modal for viewing and interacting with generated media
 * @param {boolean} props.isOpen - Modal open state
 * @param {function} props.onClose - Close handler
 * @param {object} props.media - Media object {url, type, prompt, dimensions, style, id, etc.}
 */
export default function MediaModal({ isOpen, onClose, media }) {
  const navigate = useNavigate();
  const toast = useToast();

  if (!media) return null;

  const isVideo = media.type === 'video';

  const handleUseForVideo = () => {
    if (!isVideo) {
      // Store image for video generation
      localStorage.setItem('selected_image_for_video', JSON.stringify({
        url: media.url,
        prompt: media.prompt,
        timestamp: Date.now()
      }));
      navigate('/video');
      onClose();
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = media.url;
    const extension = isVideo ? 'mp4' : 'png';
    link.download = `ai-${media.type}-${media.id || Date.now()}.${extension}`;
    link.click();
  };

  const handleShare = async () => {
    // Check if Web Share API is supported
    if (navigator.share) {
      try {
        await navigator.share({
          title: `AI Generated ${isVideo ? 'Video' : 'Image'}`,
          text: `Check out this AI-generated ${media.type}: "${media.prompt}"`,
          url: media.url,
        });
        
        toast({
          title: 'Shared successfully',
          description: `${isVideo ? 'Video' : 'Image'} shared successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Share error:', error);
          // Fallback to clipboard
          fallbackShare();
        }
      }
    } else {
      // Fallback for browsers without Web Share API
      fallbackShare();
    }
  };

  const fallbackShare = async () => {
    try {
      await navigator.clipboard.writeText(media.url);
      toast({
        title: 'Link copied',
        description: `${isVideo ? 'Video' : 'Image'} link copied to clipboard`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Clipboard error:', error);
      // Final fallback: show the URL in a prompt
      if (window.prompt) {
        window.prompt('Copy this link to share:', media.url);
      } else {
        toast({
          title: 'Share link ready',
          description: 'Please copy the URL from the address bar',
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
        window.open(media.url, '_blank');
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '6xl' }} isCentered>
      <ModalOverlay bg="rgba(0, 0, 0, 0.9)" backdropFilter="blur(10px)" />
      <ModalContent 
        bg="transparent" 
        boxShadow="none" 
        maxW={{ base: '100vw', md: '90vw' }}
        maxH={{ base: '100vh', md: '90vh' }}
        m={{ base: 0, md: 'auto' }}
      >
        <ModalCloseButton 
          color="white" 
          size={{ base: 'md', md: 'lg' }}
          top={{ base: 2, md: 4 }}
          right={{ base: 2, md: 4 }}
          zIndex={10} 
        />
        <ModalBody p={0}>
          {/* Mobile Layout */}
          <VStack 
            display={{ base: 'flex', md: 'none' }}
            spacing={4}
            h="100vh"
            justify="center"
            px={4}
            py={16}
          >
            {/* Mobile: Prompt above media */}
            <Box
              bg="rgba(0, 0, 0, 0.8)"
              p={3}
              borderRadius="md"
              w="full"
              maxW="400px"
            >
              <Text color="white" fontSize="sm" fontWeight="bold" mb={1} textAlign="center" noOfLines={2}>
                {media.prompt || 'No prompt available'}
              </Text>
            </Box>

            {/* Mobile: Media content */}
            <Box w="full" maxW="90vw">
              {isVideo ? (
                <AspectRatio ratio={16/9} maxW="90vw" maxH="50vh">
                  <video
                    src={media.url}
                    controls
                    autoPlay
                    style={{ borderRadius: "md" }}
                  />
                </AspectRatio>
              ) : (
                <MotionImage
                  src={media.url}
                  alt={media.prompt || 'Generated image'}
                  maxW="100%"
                  maxH="50vh"
                  objectFit="contain"
                  borderRadius="md"
                  fallbackSrc="/fallback-image.svg"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                />
              )}
            </Box>

            {/* Mobile: Action buttons below media */}
            <HStack spacing={2}>
              {!isVideo && (
                <IconButton
                  icon={<FaVideo />}
                  onClick={handleUseForVideo}
                  bg="black"
                  color="rgba(30, 220, 11, 1)"
                  _hover={{ bg: "black" }}
                  size="md"
                  aria-label="Use for video generation"
                />
              )}
              <IconButton
                icon={<FaDownload />}
                onClick={handleDownload}
                bg="black"
                color="rgba(30, 220, 11, 1)"
                _hover={{ bg: "black" }}
                size="md"
                aria-label={`Download ${media.type}`}
              />
              <IconButton
                icon={<FaShare />}
                onClick={handleShare}
                bg="black"
                color="rgba(30, 220, 11, 1)"
                _hover={{ bg: "black" }}
                size="md"
                aria-label="Share media"
              />
            </HStack>
          </VStack>

          {/* Desktop Layout */}
          <Box 
            display={{ base: 'none', md: 'flex' }}
            alignItems="center" 
            justifyContent="center"
            h="100%"
          >
            <Box position="relative" maxW="100%" maxH="100%">
              {isVideo ? (
                <AspectRatio ratio={16/9} maxW="80vw" maxH="70vh">
                  <video
                    src={media.url}
                    controls
                    autoPlay
                    style={{ borderRadius: "md" }}
                  />
                </AspectRatio>
              ) : (
                <MotionImage
                  src={media.url}
                  alt={media.prompt || 'Generated image'}
                  maxW="100%"
                  maxH="90vh"
                  objectFit="contain"
                  borderRadius="md"
                  fallbackSrc="/fallback-image.svg"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                />
              )}

              {/* Desktop: Action buttons overlay */}
              <HStack position="absolute" bottom={4} right={4} spacing={2}>
                {!isVideo && (
                  <IconButton
                    icon={<FaVideo />}
                    onClick={handleUseForVideo}
                    bg="black"
                    color="rgba(30, 220, 11, 1)"
                    _hover={{ bg: "black" }}
                    size="lg"
                    aria-label="Use for video generation"
                  />
                )}
                <IconButton
                  icon={<FaDownload />}
                  onClick={handleDownload}
                  bg="black"
                  color="rgba(30, 220, 11, 1)"
                  _hover={{ bg: "black" }}
                  size="lg"
                  aria-label={`Download ${media.type}`}
                />
                <IconButton
                  icon={<FaShare />}
                  onClick={handleShare}
                  bg="black"
                  color="rgba(30, 220, 11, 1)"
                  _hover={{ bg: "black" }}
                  size="lg"
                  aria-label="Share media"
                />
              </HStack>

              {/* Desktop: Media info overlay */}
              <Box
                position="absolute"
                bottom={4}
                left={4}
                bg="rgba(0, 0, 0, 0.8)"
                p={3}
                borderRadius="md"
                maxW="400px"
              >
                <Text color="white" fontSize="sm" fontWeight="bold" mb={1} noOfLines={2}>
                  {media.prompt || 'No prompt available'}
                </Text>
              </Box>
            </Box>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
